import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import AddressForm, { emptyAddress, isAddressComplete } from "@/components/AddressForm";
import { MapPin } from "lucide-react";
import { toast } from "sonner";

export default function CompleteProfile() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [address, setAddress] = useState(emptyAddress);
  const [saving, setSaving] = useState(false);

  if (!user) { navigate("/login"); return null; }

  const submit = async (e) => {
    e.preventDefault();
    if (!isAddressComplete(address)) { toast.error("Please complete your address"); return; }
    setSaving(true);
    try {
      await api.put("/auth/profile", { address });
      await refreshUser();
      const pending = JSON.parse(localStorage.getItem("ss_pending_publish") || "null");
      if (!pending) { navigate("/"); return; }
      try {
        const { data } = await api.post("/listing-chat/publish", pending);
        localStorage.removeItem("ss_pending_publish");
        if (user.deposit_status === "held") {
          toast.success("Your listing is now live!");
          navigate(`/listing/${data.id}`);
        } else {
          navigate("/deposit");
        }
      } catch (err) {
        if (err.response?.status === 402) {
          localStorage.removeItem("ss_pending_publish");
          navigate("/deposit");
        } else {
          toast.error(formatApiError(err.response?.data?.detail) || "Could not publish your listing");
          navigate("/profile");
        }
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save your profile");
    } finally { setSaving(false); }
  };

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="complete-profile-screen">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white"><MapPin className="h-6 w-6" /></div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Almost done — where should we collect and deliver?</h1>
          <p className="mt-1 text-sm text-slate-500">We use this to arrange collection and delivery for your orders.</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <AddressForm value={address} onChange={setAddress} />
          <Button type="submit" disabled={saving || !isAddressComplete(address)} data-testid="complete-profile-btn" className="w-full rounded-full bg-primary py-6 font-semibold text-white hover:bg-primary/90">
            {saving ? "Saving…" : "Complete my profile"}
          </Button>
        </form>
      </div>
    </div>
  );
}
